import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Phone, CalendarClock, Check, ChevronDown, ChevronUp } from "lucide-react";
import toast from "react-hot-toast";
import { api, type ApiResponse } from "@/api/client";
import type { CallMeta } from "@/components/customers/types";

/** The minimal communication shape the panel needs — both detail pages already have it. */
export interface CallComm {
  id: string;
  channel?: string;
  content?: string | null;
  meta?: { call?: CallMeta } | null;
  createdAt: string | Date;
}

function formatDate(iso: string | Date): string {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

/** Expandable full transcript — long bodies are R2-offloaded, fetched on demand. */
function Transcript({ commId, preview }: { commId: string; preview: string }) {
  const [open, setOpen] = useState(false);
  const { data } = useQuery({
    queryKey: ["comm-body", commId],
    queryFn: () => api.get<ApiResponse<{ body: string }>>(`/api/admin/inbox/${commId}/body`),
    enabled: open,
  });
  const text = data?.data?.body ?? preview;
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
      >
        {open ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
        {open ? "Hide transcript" : "Show transcript"}
      </button>
      {open && (
        <pre className="mt-2 max-h-64 overflow-y-auto whitespace-pre-wrap rounded bg-gray-50 p-3 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
          {text}
        </pre>
      )}
    </div>
  );
}

/**
 * Block 5.5 — "Recent calls" on the customer/lead detail: every transcribed phone call
 * with its AI summary, topic chips and the proposed follow-up (one click to apply →
 * lead nextActionAt / callback task). When the caller rings again the operator sees
 * the context instantly instead of asking everything anew.
 */
export default function RecentCallsPanel({
  communications,
  invalidateKeys,
}: {
  communications: CallComm[];
  /** React Query keys to refresh after a follow-up is applied (page-specific). */
  invalidateKeys: unknown[][];
}) {
  const queryClient = useQueryClient();
  const applyFollowUp = useMutation({
    mutationFn: (commId: string) =>
      api.post<ApiResponse<{ applied: boolean }>>(`/api/admin/inbox/${commId}/apply-follow-up`, {}),
    onSuccess: () => {
      toast.success("Follow-up scheduled");
      for (const key of invalidateKeys) queryClient.invalidateQueries({ queryKey: key });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to apply follow-up"),
  });

  const calls = communications.filter((c) => c.meta?.call);
  if (calls.length === 0) return null;

  return (
    <div className="card">
      <div className="mb-4 flex items-center gap-2">
        <Phone className="h-5 w-5 text-green-600" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Recent calls</h2>
      </div>
      <div className="space-y-4">
        {calls.map((comm) => {
          const call = comm.meta!.call!;
          return (
            <div
              key={comm.id}
              className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
            >
              <div className="mb-1 flex flex-wrap items-center gap-2 text-sm">
                <span className="font-medium text-gray-900 dark:text-white">
                  {formatDate(comm.createdAt)}
                </span>
                {call.callerName && (
                  <span className="text-gray-500 dark:text-gray-400">{call.callerName}</span>
                )}
                {call.callerPhone && (
                  <span className="text-gray-400 dark:text-gray-500">{call.callerPhone}</span>
                )}
              </div>
              {call.summary && (
                <p className="mb-2 text-sm text-gray-700 dark:text-gray-300">{call.summary}</p>
              )}
              {(call.topics?.length ?? 0) > 0 && (
                <div className="mb-2 flex flex-wrap gap-1">
                  {call.topics!.map((t) => (
                    <span
                      key={t}
                      className="rounded bg-green-50 px-1.5 py-0.5 text-[11px] font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400"
                    >
                      {t}
                    </span>
                  ))}
                </div>
              )}
              {call.followUpAt && (
                <div className="mb-2 flex flex-wrap items-center gap-2 rounded bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                  <CalendarClock className="h-3.5 w-3.5 shrink-0" />
                  <span>
                    Proposed follow-up {formatDate(call.followUpAt)}
                    {call.followUpNote ? ` — ${call.followUpNote}` : ""}
                  </span>
                  {call.followUpAppliedAt ? (
                    <span className="ml-auto flex items-center gap-1 font-medium text-green-700 dark:text-green-400">
                      <Check className="h-3.5 w-3.5" /> Scheduled
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => applyFollowUp.mutate(comm.id)}
                      disabled={applyFollowUp.isPending}
                      className="ml-auto rounded bg-amber-600 px-2 py-0.5 font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                    >
                      Apply
                    </button>
                  )}
                </div>
              )}
              <Transcript commId={comm.id} preview={comm.content ?? ""} />
            </div>
          );
        })}
      </div>
    </div>
  );
}
